import { SJCLBitArray, fromBitsToArrayBuffer, isSJCLBitArray } from './sjcl-bit-array.type';

/**
 * The ArrayBufferBuilder class facilitates the construction
 * of a single aggregated ArrayBuffer from multiple sources.
 */
export class ArrayBufferBuilder {
  private buffers: ArrayBuffer[] = [];

  /**
   * Adds a buffer to the internal storage.
   *
   * @param {ArrayBuffer | ArrayBufferView | SJCLBitArray} buffer - The buffer to be added. It can be an ArrayBuffer, ArrayBufferView, or SJCLBitArray.
   * @return {this} - Returns the current instance for chaining.
   */
  public add(buffer: ArrayBuffer | ArrayBufferView | SJCLBitArray): this {
    if (buffer instanceof ArrayBuffer) {
      this.buffers.push(buffer);
    } else if (ArrayBuffer.isView(buffer)) {
      this.buffers.push(buffer.buffer.slice(buffer.byteOffset, buffer.byteOffset + buffer.byteLength));
    } else if (isSJCLBitArray(buffer)) {
      this.buffers.push(fromBitsToArrayBuffer(buffer));
    } else {
      throw new Error('Invalid buffer type');
    }
    return this;
  }

  /**
   * Combines multiple ArrayBuffer objects from the buffers property into a single Uint8Array.
   *
   * @return {Uint8Array} The combined Uint8Array representing the contents of all buffers.
   */
  public build(): Uint8Array {
    const totalLength = this.buffers.reduce((sum, buffer) => sum + buffer.byteLength, 0);
    const combinedBuffer = new Uint8Array(totalLength);

    let offset = 0;
    for (const buffer of this.buffers) {
      combinedBuffer.set(new Uint8Array(buffer), offset);
      offset += buffer.byteLength;
    }

    return combinedBuffer;
  }
}
